import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Modal from '@mui/material/Modal';
import Pagination from '@mui/material/Pagination';
import Tooltip from '@mui/material/Tooltip';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import BulkDismissHitsModal from 'components/BiteDB/Modal/BulkDismissHitsModal';
import DismissHitModal from 'components/BiteDB/Modal/DismissHitModal';
import ScreeningDialog from 'components/ScreeningComposer/ScreeningDialog';
import ResultsToolbar, {
  FilterChip,
} from 'components/ScreeningComposer/ResultsToolbar';
import {
  BulkHitSelection,
  BulkHitVisibilityOperation,
  getBulkHitSelectionKey,
} from 'components/BiteDB/Tables/bulkHitSelection';
import { parseMatchScoreFilter } from 'components/BiteDB/Tables/entityHitList';
import {
  ENTITY_HITS_PAGE_SIZE,
  entityHitsPageCount,
  interactiveHitsDatasetForTab,
  resolveEntityHitsPageChange,
} from './entityHitsPagination';
import { resolveEntityToolTableChrome } from './entityTableFilterChrome';
import {
  ActiveSavedEntityFilter,
  countEntityTableFilters,
  EMPTY_ENTITY_TABLE_FILTERS,
  EntityFilterKey,
  EntityTableFilterSelection,
  entityTableFiltersEqual,
  fromSavedEntityFilter,
} from './entityTableFilterParams';
import type { EntityTableExportContext } from './entityTableFilterParams';
import type { ISearchFilter } from 'providers/GlobalProvider';
import { useEntityScreeningLenses } from 'hooks/useEntityScreeningLenses';
import { getEntityMatchScore } from 'utils/nameMatchScore';
import { splitHitMeaning, splitRiskSignals } from 'utils/entityRiskSignals';
import {
  formatEntityLensCode,
  getEntityLensCode,
  getEntityLensName,
  getEntityLensBadgeClassName,
  parseEntityLensNames,
} from 'utils/entityLensBadge';

interface IEntityScreeningResultsTableProps {
  results: any;
  ownershipResults?: any;
  page: number;
  setPage: any;
  handleSubmit: any;
  searchTerm?: string;
  entityFilterOptions?: any;
  variant?: 'entity' | 'product';
  forcedTab?: 'All Matches' | 'Ownership';
  hideToolbar?: boolean;
  actionsPortalTarget?: HTMLElement | null;
  externalSearchQuery?: string;
  onResultCountChange?: (count: number) => void;
  // Static mode renders already-loaded data (e.g. a saved Saved Work ticket)
  // entirely client-side: the in-table search/pagination filter the loaded
  // rows instead of re-querying the screening API via handleSubmit.
  isStatic?: boolean;
  // Server-paginated static data still uses local search/filtering, but the
  // supplied rows already represent one backend page and must not be sliced a
  // second time in the browser.
  serverPaginated?: boolean;
  // Supplied when the parent pushes Table Filters into the screening query
  // (`scoreParams.tableFilters`). The table still applies them to the rows it
  // renders — so the columns stay exact and a service that does not yet know
  // the param can never show unfiltered rows — but Hit Total, the tab badges
  // and the pager then describe the filtered result set instead of one page.
  onTableFiltersChange?: (filters: EntityTableFilterSelection) => void;
  controlledTableFilters?: EntityTableFilterSelection;
  // Database Info entity hits: Entity Country + Source Country, Date Updated,
  // multi-lens pills, match-score filters, and an optional Remove Hit column.
  layout?: 'default' | 'entityHits';
  onToggleHitVisibility?: (
    id: string,
    reason?: string,
    recordName?: string,
  ) => void;
  removedHitIds?: string[];
  onDownloadResults?: () => void;
  bulkSelectedHits?: Map<string, BulkHitSelection>;
  setBulkSelectedHits?: React.Dispatch<
    React.SetStateAction<Map<string, BulkHitSelection>>
  >;
  parentDatabaseEntityId?: string;
  parentEntityName?: string;
  onBulkUpdateHits?: (
    operation: BulkHitVisibilityOperation,
    comment: string,
  ) => Promise<void>;
  isBulkHiding?: boolean;
  // Entity Screening V2 is opt-in so Monitor, BITE DB, Export Hub and saved
  // ticket consumers retain their existing table chrome.
  appearance?: 'default' | 'screening-v2';
  onExport?: (context: EntityTableExportContext) => void;
  activeTabRequest?: {
    tab: 'All Matches' | 'Ownership';
    nonce: number;
  } | null;
  requestedSearchQuery?: string;
  onOpenAsOfDate?: () => void;
  onClearAsOfDate?: () => void;
  asOfLabel?: string;
  savedFilters?: ISearchFilter[];
  onSaveFilterRequest?: (filters: EntityTableFilterSelection) => void;
  onDeleteSavedFilter?: (id: string) => void;
  // Resolves true once the update is saved.
  onUpdateSavedFilter?: (
    filter: ISearchFilter,
    filters: EntityTableFilterSelection,
  ) => Promise<boolean>;
  // Supplied when the parent owns the pressed Saved Filter, so it survives
  // the table remounting while a screen re-runs.
  activeSavedFilter?: ActiveSavedEntityFilter | null;
  onActiveSavedFilterChange?: (next: ActiveSavedEntityFilter | null) => void;
  // Entity Screening: the screen's own filters (lens, source country, agency,
  // list) take over the toolbar's Filters, chips and Reset. They re-run the
  // screen, so they replace the column filters rather than add to them.
  // `onOpen` is left out where they don't apply (a Record ID Search).
  screenFilterControls?: {
    count: number;
    label?: string;
    chips: FilterChip[];
    onOpen?: () => void;
    onClear: () => void;
  };
}

type EntitySortKey =
  | 'name'
  | 'entityType'
  | 'relatedEntity'
  | 'country'
  | 'sourceCountry'
  | 'lensCode'
  | 'matchSource'
  | 'score'
  | 'detectionDate';

type TableColumnKey = EntitySortKey | 'visibility' | 'bulkSelect';
type TableColumn = {
  key: TableColumnKey;
  label: string;
  sortable?: boolean;
};

type SortDirection = 'asc' | 'desc';

type ScoreRange = { min: string; max: string };

const EMPTY_SCORE_RANGE: ScoreRange = { min: '', max: '' };

const getHitRecordId = (source: Record<string, unknown>, fallback: string) =>
  String(source['BITE Record ID'] ?? source.id ?? fallback);

const EMPTY_BULK_HIT_SELECTIONS = new Map<string, BulkHitSelection>();

const getSource = (hit: any) => hit?._source || {};

const getValue = (source: any, keys: string[], fallback = '--') => {
  const value = keys.map((key) => source?.[key]).find(Boolean);
  return value || fallback;
};

const getScoreBand = (score: number | null) => {
  if (score === null) return 'none';
  if (score >= 90) return 'green';
  if (score >= 75) return 'yellow';
  if (score >= 55) return 'orange';
  return 'red';
};

// OpenSearch stores exact timestamps, so rendering them raw shows a different
// calendar day depending on the viewer's local timezone. Normalize every date
// to US Eastern (EST/EDT) so the date displayed is the same for every user
// regardless of where they are in the world.
const EST_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

const formatDateEST = (value: any): string => {
  if (value === null || value === undefined) return '--';
  const raw = String(value).trim();
  if (!raw || raw === '--') return raw || '--';
  // A date-only string (no time component) carries no timezone meaning — render
  // it as-is so we don't shift it onto the previous/next calendar day.
  const hasTimeComponent = /\d{1,2}:\d{2}/.test(raw) || /T\d/.test(raw);
  if (!hasTimeComponent) return raw;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return EST_DATE_FORMATTER.format(parsed);
};

const getEntityType = (source: any) => {
  const explicit = getValue(source, ['Entity Type', 'Type'], '');
  if (explicit) return explicit;
  return source?.['Date of Birth'] ? 'Individual' : 'Entity';
};

const decorateEntityHits = (
  hitList: any[],
  args: {
    effectiveSearchTerm: string;
    isProductTable: boolean;
    isEntityHitsLayout: boolean;
    removedHitIdSet: Set<string>;
  },
) =>
  hitList.map((hit: any, index: number) => {
    const source = getSource(hit);
    const lensNames = parseEntityLensNames(source);
    const lensCode = getEntityLensCode(source);
    const score = args.isProductTable
      ? null
      : getEntityMatchScore(args.effectiveSearchTerm, source);
    const hitId = getHitRecordId(source, String(hit?._id ?? index));
    return {
      hit: hit,
      source: source,
      score: score,
      lensCode: lensCode,
      lensNames: lensNames,
      lensValue: getEntityLensName(source),
      name: args.isProductTable
        ? getProductName(source)
        : getValue(source, ['Name of Entity']),
      entityType: args.isProductTable
        ? getProductCode(source)
        : getEntityType(source),
      relatedEntity: getValue(source, ['Related Entity']),
      country: getValue(source, ['Entity Country']),
      sourceCountry: getValue(source, ['Source Country']),
      matchSource: getValue(source, ['List Name', 'Agency', 'Dataset']),
      detectionDate: getValue(source, [
        'Date Updated on BITE Platform',
        'Date Updated',
      ]),
      hitId: hitId,
      removed: Boolean(hitId && args.removedHitIdSet.has(hitId)),
    };
  });

const rowMatchesEntityTableFilters = (
  row: any,
  args: {
    isEntityHitsLayout: boolean;
    tableFilters: Record<EntityFilterKey, string[]>;
    minScore: number | null;
    maxScore: number | null;
    staticSearchQuery: string;
  },
) => {
  const entityTypeMatch =
    args.isEntityHitsLayout ||
    args.tableFilters.entityType.length === 0 ||
    args.tableFilters.entityType.includes(row.entityType);
  const countryMatch =
    args.tableFilters.country.length === 0 ||
    args.tableFilters.country.includes(row.country);
  // Match on the parsed lens names first: `lensCode` is only the first lens
  // and `lensValue` is the raw field, so a record carrying several lenses in
  // one delimited string matches neither.
  const lensMatch =
    args.tableFilters.lensCode.length === 0 ||
    (row.lensNames || []).some((lens: string) =>
      args.tableFilters.lensCode.includes(lens),
    ) ||
    args.tableFilters.lensCode.includes(row.lensCode) ||
    args.tableFilters.lensCode.includes(row.lensValue);
  const listNameMatch =
    args.tableFilters.matchSource.length === 0 ||
    args.tableFilters.matchSource.includes(row.matchSource);
  const scoreMatch =
    (args.minScore === null ||
      (row.score !== null && Number(row.score) >= args.minScore)) &&
    (args.maxScore === null ||
      (row.score !== null && Number(row.score) <= args.maxScore));
  const staticSearchMatch =
    !args.staticSearchQuery ||
    [
      row.name,
      row.entityType,
      row.matchSource,
      row.lensValue,
      row.lensCode,
      ...(row.lensNames || []),
      row.country,
      row.sourceCountry,
    ]
      .filter(Boolean)
      .some((field: string) =>
        String(field).toLowerCase().includes(args.staticSearchQuery),
      );

  return (
    entityTypeMatch &&
    countryMatch &&
    lensMatch &&
    listNameMatch &&
    scoreMatch &&
    staticSearchMatch
  );
};

const getProductName = (source: any) =>
  getValue(source, [
    'Product Name',
    'Product',
    'Commodity Name',
    'Description',
    'description',
    'Name of Entity',
    'name',
  ]);

const getProductCode = (source: any) =>
  getValue(source, [
    'HTS Code',
    'HTS',
    'HS Code',
    'HS CODE',
    'Harmonized Code',
    'harmonizedScheduleCode',
    'htsCode',
    'hsCode',
    'code',
    'Entity Type',
  ]);

const hasDisplayValue = (value: any) => {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) return value.filter(Boolean).length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return String(value).trim() !== '';
};

const formatFieldValue = (value: any): string => {
  if (Array.isArray(value)) return value.filter(Boolean).join(', ');
  if (value && typeof value === 'object') return JSON.stringify(value);
  return String(value ?? '');
};

// Detects a URL-looking token. Accepts bare domains starting with www. and any
// http(s) link. Keep the regex narrow — we render arbitrary record fields and
// don't want stray text turned into links.
const URL_TOKEN_REGEX = /\b((?:https?:\/\/|www\.)[^\s<>"'`]+)/gi;

const renderFieldWithLinks = (value: string): React.ReactNode => {
  if (!value) return value;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;
  const text = value;
  for (const match of text.matchAll(URL_TOKEN_REGEX)) {
    const url = match[0];
    const start = match.index ?? 0;
    if (start > lastIndex) {
      parts.push(text.slice(lastIndex, start));
    }
    // Strip trailing punctuation that's almost certainly sentence terminators
    // rather than part of the URL.
    let cleaned = url;
    let trailing = '';
    while (
      cleaned.length > 0 &&
      '.,);:!?'.includes(cleaned[cleaned.length - 1])
    ) {
      trailing = cleaned[cleaned.length - 1] + trailing;
      cleaned = cleaned.slice(0, -1);
    }
    const href = cleaned.startsWith('http') ? cleaned : `https://${cleaned}`;
    parts.push(
      <a
        key={`url-${key++}`}
        href={href}
        target='_blank'
        rel='noopener noreferrer'
        className='link'
      >
        {cleaned}
      </a>,
    );
    if (trailing) parts.push(trailing);
    lastIndex = start + url.length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length > 0 ? parts : value;
};

const sortIcon = (direction: SortDirection, active = true) => {
  const isAsc = direction === 'asc';
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 16 16'
      fill='none'
      aria-hidden='true'
      className={active ? 'active' : ''}
    >
      <path
        d={
          isAsc
            ? 'M8 12.6666V3.33331M8 3.33331L3.33333 7.99998M8 3.33331L12.6667 7.99998'
            : 'M8 3.33331V12.6666M8 12.6666L3.33333 7.99998M8 12.6666L12.6667 7.99998'
        }
        stroke='currentColor'
        strokeWidth='1.33'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
};

const EntityScreeningResultsTable: React.FC<
  IEntityScreeningResultsTableProps
> = ({
  results,
  ownershipResults,
  page,
  setPage,
  handleSubmit,
  searchTerm,
  isStatic = false,
  variant = 'entity',
  forcedTab,
  hideToolbar = false,
  actionsPortalTarget,
  externalSearchQuery,
  onResultCountChange,
  serverPaginated = false,
  onTableFiltersChange,
  controlledTableFilters,
  layout = 'default',
  onToggleHitVisibility,
  removedHitIds = [],
  onDownloadResults,
  bulkSelectedHits = EMPTY_BULK_HIT_SELECTIONS,
  setBulkSelectedHits,
  parentDatabaseEntityId,
  parentEntityName,
  onBulkUpdateHits,
  isBulkHiding = false,
  appearance = 'default',
  onExport,
  activeTabRequest,
  requestedSearchQuery,
  onOpenAsOfDate,
  onClearAsOfDate,
  asOfLabel,
  savedFilters = [],
  onSaveFilterRequest,
  onDeleteSavedFilter,
  onUpdateSavedFilter,
  activeSavedFilter: controlledActiveSavedFilter,
  onActiveSavedFilterChange,
  screenFilterControls,
}) => {
  const isScreeningV2 = appearance === 'screening-v2';
  const isEntityHitsLayout = layout === 'entityHits';
  const { lenses: vocabularyLenses } =
    useEntityScreeningLenses(isEntityHitsLayout);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('All Matches');
  const [selectedHit, setSelectedHit] = useState<any>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [dismissTarget, setDismissTarget] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [isBulkReviewOpen, setIsBulkReviewOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState<{
    key: EntitySortKey | '';
    direction: SortDirection;
  }>({
    key: isEntityHitsLayout ? 'score' : '',
    direction: isEntityHitsLayout ? 'desc' : 'asc',
  });
  const [scoreRange, setScoreRange] = useState<ScoreRange>(EMPTY_SCORE_RANGE);
  const [draftScoreRange, setDraftScoreRange] =
    useState<ScoreRange>(EMPTY_SCORE_RANGE);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [tableFilters, setTableFilters] = useState<
    Record<EntityFilterKey, string[]>
  >(controlledTableFilters || EMPTY_ENTITY_TABLE_FILTERS);
  const [draftTableFilters, setDraftTableFilters] = useState<
    Record<EntityFilterKey, string[]>
  >(controlledTableFilters || EMPTY_ENTITY_TABLE_FILTERS);
  const [filterSearches, setFilterSearches] = useState<
    Record<EntityFilterKey, string>
  >({
    entityType: '',
    country: '',
    lensCode: '',
    matchSource: '',
  });
  const [localActiveSavedFilter, setLocalActiveSavedFilter] =
    useState<ActiveSavedEntityFilter | null>(null);
  const activeSavedFilter = onActiveSavedFilterChange
    ? controlledActiveSavedFilter ?? null
    : localActiveSavedFilter;
  const setActiveSavedFilter = onActiveSavedFilterChange
    ? onActiveSavedFilterChange
    : setLocalActiveSavedFilter;

  useEffect(() => {
    if (!controlledTableFilters) return;
    setTableFilters(controlledTableFilters);
    setDraftTableFilters(controlledTableFilters);
  }, [controlledTableFilters]);

  useEffect(() => {
    if (
      activeSavedFilter &&
      !entityTableFiltersEqual(tableFilters, activeSavedFilter.selection)
    ) {
      setActiveSavedFilter(null);
    }
  }, [tableFilters, activeSavedFilter, setActiveSavedFilter]);

  useEffect(() => {
    if (
      activeSavedFilter &&
      !savedFilters.some(
        (filter) => filter._id === activeSavedFilter.filter._id,
      )
    ) {
      setActiveSavedFilter(null);
    }
  }, [savedFilters, activeSavedFilter, setActiveSavedFilter]);

  const resolvedActiveTab = forcedTab ?? activeTab;
  const resolvedSearchQuery = externalSearchQuery ?? searchQuery;
  const isOwnershipTab = resolvedActiveTab === 'Ownership';
  const getExportContext = (): EntityTableExportContext => ({
    activeTab: resolvedActiveTab === 'Ownership' ? 'Ownership' : 'All Matches',
    searchQuery: resolvedSearchQuery,
    tableFilters: tableFilters,
  });
  const isProductTable = variant === 'product';
  const entityHits = results?.data?.[0]?.result?.body?.hits?.hits || [];
  const entityTotal = results?.data?.[0]?.result?.body?.hits?.total?.value || 0;
  const ownershipHits =
    ownershipResults?.data?.[0]?.result?.body?.hits?.hits || [];
  const ownershipTotal =
    ownershipResults?.data?.[0]?.result?.body?.hits?.total?.value || 0;
  const activeResultsLoading = isOwnershipTab
    ? ownershipResults?.isLoading
    : results?.isLoading;

  // The score reflects how closely a result's name matches the name being
  // screened. Prefer the screening entity name; fall back to the in-table
  // search box only when no entity name was provided.
  const effectiveSearchTerm =
    String(searchTerm || '').trim() || resolvedSearchQuery.trim();

  const removedHitIdSet = useMemo(
    () => new Set(removedHitIds.map((id) => String(id))),
    [removedHitIds],
  );

  const decorateArgs = {
    effectiveSearchTerm: effectiveSearchTerm,
    isProductTable: isProductTable,
    isEntityHitsLayout: isEntityHitsLayout,
    removedHitIdSet: removedHitIdSet,
  };
  const entityDecoratedRows = useMemo(
    () => decorateEntityHits(entityHits, decorateArgs),
    [
      entityHits,
      effectiveSearchTerm,
      isProductTable,
      isEntityHitsLayout,
      removedHitIdSet,
    ],
  );
  const ownershipDecoratedRows = useMemo(
    () => decorateEntityHits(ownershipHits, decorateArgs),
    [
      ownershipHits,
      effectiveSearchTerm,
      isProductTable,
      isEntityHitsLayout,
      removedHitIdSet,
    ],
  );
  const decoratedRows = isOwnershipTab
    ? ownershipDecoratedRows
    : entityDecoratedRows;

  // Facet buckets for the active dataset. They are computed before the
  // `tableFilters` post_filter, so they describe the whole screen rather than
  // the loaded page — without them a server-paginated table could only offer
  // the values that happen to sit on page 1, and selecting one would hide
  // every other option.
  const facetBuckets = useMemo(() => {
    const aggregations = (isOwnershipTab ? ownershipResults : results)
      ?.data?.[0]?.result?.body?.aggregations;
    const bucketKeys = (agg: any): string[] =>
      Array.isArray(agg?.buckets)
        ? agg.buckets
            .map((bucket: any) => String(bucket?.key ?? '').trim())
            .filter(Boolean)
        : [];

    return {
      entityType: bucketKeys(aggregations?.entityTypeAgg),
      country: bucketKeys(aggregations?.entityCountryAgg),
      lensCode: bucketKeys(aggregations?.lensAgg),
      matchSource: bucketKeys(aggregations?.listNameAgg),
    };
  }, [results, ownershipResults, isOwnershipTab]);

  const filterOptions = useMemo(() => {
    const sortUnique = (values: string[]) =>
      Array.from(
        new Set(values.filter((value) => value && value !== '--')),
      ).sort((a, b) => String(a).localeCompare(String(b)));

    // Union of the facets and the values on screen: the facets can miss a
    // value the table derives (an Entity Type read off Date of Birth), and
    // the rows miss everything on the pages that are not loaded.
    const buildOptions = (key: EntityFilterKey) =>
      sortUnique([
        ...facetBuckets[key],
        ...decoratedRows.map((row: any) => row[key]),
      ]);

    // Lens options are full BITE Lens names on every layout — that is what
    // the query filters on, and a record with several lenses is only
    // selectable by name (its `lensCode` is just the first one).
    const lensOptions = sortUnique([
      ...facetBuckets.lensCode,
      ...decoratedRows.flatMap((row: any) => row.lensNames || []),
      ...(isEntityHitsLayout ? vocabularyLenses : []),
    ]);

    return {
      entityType: buildOptions('entityType'),
      country: buildOptions('country'),
      lensCode: lensOptions,
      matchSource: buildOptions('matchSource'),
    };
  }, [decoratedRows, facetBuckets, isEntityHitsLayout, vocabularyLenses]);

  // In static mode the in-table search box can't re-query the server, so it
  // filters the already-loaded rows by name / source / lens / country.
  const staticSearchQuery = isStatic
    ? resolvedSearchQuery.trim().toLowerCase()
    : '';

  const minScore = parseMatchScoreFilter(scoreRange.min);
  const maxScore = parseMatchScoreFilter(scoreRange.max);
  const scoreFilterCount =
    (minScore !== null ? 1 : 0) + (maxScore !== null ? 1 : 0);
  const columnFilterCount = countEntityTableFilters(tableFilters);
  const activeFilterCount = columnFilterCount + scoreFilterCount;
  const shownFilterCount = screenFilterControls
    ? screenFilterControls.count
    : activeFilterCount;
  // Match Score is computed in the browser from the searched name, so it can
  // never be part of the query — it stays a view on the loaded page even when
  // the column filters are pushed into OpenSearch.
  const filtersRunOnServer = Boolean(onTableFiltersChange);
  const clientOnlyFilterCount = filtersRunOnServer
    ? scoreFilterCount
    : activeFilterCount;

  const filterMatchArgs = {
    isEntityHitsLayout: isEntityHitsLayout,
    tableFilters: tableFilters,
    minScore: minScore,
    maxScore: maxScore,
    staticSearchQuery: staticSearchQuery,
  };
  const entityFilteredRows = entityDecoratedRows.filter((row: any) =>
    rowMatchesEntityTableFilters(row, filterMatchArgs),
  );
  const ownershipFilteredRows = ownershipDecoratedRows.filter((row: any) =>
    rowMatchesEntityTableFilters(row, filterMatchArgs),
  );
  const filteredRows = isOwnershipTab
    ? ownershipFilteredRows
    : entityFilteredRows;

  const hasMountedSearch = useRef(false);
  const lastSubmittedSearch = useRef('');

  useEffect(() => {
    // Static mode filters loaded rows client-side; never hit the server.
    if (isStatic) {
      return undefined;
    }
    if (!hasMountedSearch.current) {
      hasMountedSearch.current = true;
      return undefined;
    }

    const nextSearch = resolvedSearchQuery.trim();
    if (nextSearch.length > 0 && nextSearch.length < 3) {
      return undefined;
    }
    if (nextSearch === lastSubmittedSearch.current) {
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      lastSubmittedSearch.current = nextSearch;
      setPage(0);
      // Empty box -> undefined so the screen falls back to the screened entity
      // (inputs.primary) instead of an empty-string query that matches all docs.
      handleSubmit(0, nextSearch || undefined, { runAiEvaluation: false });
    }, 850);

    return () => window.clearTimeout(timeout);
  }, [resolvedSearchQuery]);

  useEffect(() => {
    if (requestedSearchQuery === undefined) {
      return;
    }
    setSearchQuery(requestedSearchQuery);
  }, [requestedSearchQuery]);

  useEffect(() => {
    if (!activeTabRequest) {
      return;
    }
    setActiveTab(activeTabRequest.tab);
    setPage(0);
    if (!isStatic) {
      handleSubmit(0, undefined, {
        runAiEvaluation: false,
        dataset: interactiveHitsDatasetForTab(activeTabRequest.tab),
      });
    }
  }, [activeTabRequest?.nonce]);

  // Static mode: jump back to the first page whenever the client-side search
  // or column filters change so the visible window always starts at the top.
  useEffect(() => {
    if (isStatic) {
      setPage(0);
    }
  }, [resolvedSearchQuery, tableFilters, scoreRange, resolvedActiveTab]);

  const sortedRows = useMemo(() => {
    const compareRows = (a: any, b: any) => {
      if (!sortConfig.key) {
        return 0;
      }
      const aValue = a[sortConfig.key as EntitySortKey];
      const bValue = b[sortConfig.key as EntitySortKey];
      const directionMultiplier = sortConfig.direction === 'asc' ? 1 : -1;

      if (sortConfig.key === 'score') {
        return (
          ((Number(aValue) || 0) - (Number(bValue) || 0)) * directionMultiplier
        );
      }

      if (sortConfig.key === 'detectionDate') {
        const aTime = Date.parse(aValue);
        const bTime = Date.parse(bValue);
        if (!Number.isNaN(aTime) && !Number.isNaN(bTime)) {
          return (aTime - bTime) * directionMultiplier;
        }
      }

      if (sortConfig.key === 'lensCode' && isEntityHitsLayout) {
        return (
          String((a.lensNames || []).join(', ')).localeCompare(
            String((b.lensNames || []).join(', ')),
          ) * directionMultiplier
        );
      }

      return (
        String(aValue || '').localeCompare(String(bValue || '')) *
        directionMultiplier
      );
    };

    const sorted = [...filteredRows].sort(compareRows);
    if (!isEntityHitsLayout) {
      return sorted;
    }
    return [
      ...sorted.filter((row: any) => !row.removed),
      ...sorted.filter((row: any) => row.removed),
    ];
  }, [filteredRows, sortConfig, isEntityHitsLayout]);

  // Server-side pagination: one `pageSize` window per request. Page count is
  // capped at the OpenSearch from+size window so the control never offers
  // jump-to-last beyond what normal paging can fetch. Filters the browser
  // applies on its own narrow the loaded rows only, so they constrain tab
  // badges and the pager just for a client-paginated table that holds the
  // whole result set; filters that went into the query are already in the
  // backend Hit Total.
  const pageSize = ENTITY_HITS_PAGE_SIZE;
  const usesClientPagination = isStatic && !serverPaginated;
  // `isPageScopedFiltering`: a server-backed table — the Tool's own True
  // Pagination included — filters only the page it holds, so the footer
  // reports that page's matches against the untouched backend total.
  const { tabCounts, totalForPagination, isPageScopedFiltering } =
    resolveEntityToolTableChrome({
      isEntityHitsLayout: isEntityHitsLayout,
      isProductTable: isProductTable,
      isOwnershipTab: isOwnershipTab,
      clientOnlyFilterCount: clientOnlyFilterCount,
      hasClientSearchQuery: Boolean(staticSearchQuery),
      usesClientPagination: usesClientPagination,
      entityHitTotal: entityTotal,
      ownershipHitTotal: ownershipTotal,
      entityFilteredCount: entityFilteredRows.length,
      ownershipFilteredCount: ownershipFilteredRows.length,
      clientPagedRowCount: sortedRows.length,
    });
  useEffect(() => {
    const isNarrowedInBrowser =
      clientOnlyFilterCount > 0 || Boolean(staticSearchQuery);
    const badgeCount =
      usesClientPagination && isEntityHitsLayout && !isNarrowedInBrowser
        ? entityTotal
        : totalForPagination;
    onResultCountChange?.(badgeCount);
  }, [
    clientOnlyFilterCount,
    entityTotal,
    isEntityHitsLayout,
    onResultCountChange,
    staticSearchQuery,
    totalForPagination,
    usesClientPagination,
  ]);
  const pagerMath = {
    hitTotal: totalForPagination,
    pageSize: pageSize,
  };
  const pageCount = entityHitsPageCount(pagerMath);
  const currentPage = Math.min(Math.max(page, 0), pageCount - 1);
  const pageStart = currentPage * pageSize;
  // Fully static data is sliced in the browser. In either server-backed mode,
  // the supplied rows are already the requested page.
  const pagedRows = usesClientPagination
    ? sortedRows.slice(pageStart, pageStart + pageSize)
    : sortedRows;

  const showBulkSelection = Boolean(
    isEntityHitsLayout && parentDatabaseEntityId && setBulkSelectedHits,
  );

  const toBulkSelection = (row: {
    hitId: string;
    name: string;
    removed: boolean;
  }): BulkHitSelection | null => {
    if (!parentDatabaseEntityId || !row.hitId) {
      return null;
    }
    return {
      key: getBulkHitSelectionKey(parentDatabaseEntityId, row.hitId),
      databaseEntityId: parentDatabaseEntityId,
      entityName: parentEntityName || effectiveSearchTerm || 'Entity',
      hitId: row.hitId,
      hitName: row.name || 'Unnamed hit',
      operation: row.removed ? 'unhide' : 'hide',
    };
  };

  const bulkSelections = useMemo(
    () => Array.from(bulkSelectedHits.values()),
    [bulkSelectedHits],
  );
  const bulkOperation = bulkSelections[0]?.operation;
  const pageSelectionOperation: BulkHitVisibilityOperation =
    bulkOperation ||
    (pagedRows.some((row: { removed: boolean }) => !row.removed)
      ? 'hide'
      : 'unhide');
  const selectablePageHits = useMemo(
    () =>
      showBulkSelection
        ? pagedRows
            .map(toBulkSelection)
            .filter(
              (
                selection: BulkHitSelection | null,
              ): selection is BulkHitSelection =>
                Boolean(
                  selection && selection.operation === pageSelectionOperation,
                ),
            )
        : [],
    [
      showBulkSelection,
      pagedRows,
      parentDatabaseEntityId,
      parentEntityName,
      effectiveSearchTerm,
      pageSelectionOperation,
    ],
  );
  const selectedPageHitCount = selectablePageHits.reduce(
    (count: number, selection: { key: string }) =>
      count + (bulkSelectedHits.has(selection.key) ? 1 : 0),
    0,
  );
  const isPageSelected =
    selectablePageHits.length > 0 &&
    selectedPageHitCount === selectablePageHits.length;
  const isPageIndeterminate = selectedPageHitCount > 0 && !isPageSelected;
  const selectedEntityCount = useMemo(
    () =>
      new Set(bulkSelections.map((selection) => selection.databaseEntityId))
        .size,
    [bulkSelections],
  );

  const handleToggleBulkHit = (row: {
    hitId: string;
    name: string;
    removed: boolean;
  }) => {
    const selection = toBulkSelection(row);
    if (!selection || !setBulkSelectedHits) {
      return;
    }
    setBulkSelectedHits((previous) => {
      const next = new Map(previous);
      if (next.has(selection.key)) {
        next.delete(selection.key);
      } else if (
        next.size > 0 &&
        next.values().next().value?.operation !== selection.operation
      ) {
        return previous;
      } else {
        next.set(selection.key, selection);
      }
      return next;
    });
  };

  const handleTogglePageSelection = () => {
    if (!setBulkSelectedHits || selectablePageHits.length === 0) {
      return;
    }
    setBulkSelectedHits((previous) => {
      const next = new Map(previous);
      if (isPageSelected) {
        selectablePageHits.forEach((selection) => next.delete(selection.key));
      } else {
        selectablePageHits.forEach((selection) =>
          next.set(selection.key, selection),
        );
      }
      return next;
    });
  };

  const handleRemoveBulkSelection = (key: string) => {
    setBulkSelectedHits?.((previous) => {
      const next = new Map(previous);
      next.delete(key);
      return next;
    });
  };

  const handleClearBulkSelection = () => {
    setBulkSelectedHits?.(new Map());
  };

  const handleConfirmBulkUpdate = async (comment: string) => {
    if (!onBulkUpdateHits || !bulkOperation) {
      return;
    }
    await onBulkUpdateHits(bulkOperation, comment);
    setIsBulkReviewOpen(false);
  };

  const handleSort = (key: EntitySortKey) => {
    setSortConfig((prev) => ({
      key: key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const getSortIcon = (key: EntitySortKey) => {
    if (sortConfig.key !== key) {
      return sortIcon('asc', false);
    }
    return sortIcon(sortConfig.direction);
  };

  // The Ownership tab swaps Detection Date out for a Related Entity column
  // (placed just right of Entity Type); every other tab keeps Detection Date.
  const columnHeaders: TableColumn[] = isProductTable
    ? [
        { key: 'name', label: 'Product / Match' },
        { key: 'entityType', label: 'HTS / HS Code' },
        { key: 'country', label: 'Country' },
        { key: 'lensCode', label: 'BITE Lens' },
        { key: 'matchSource', label: 'List Name' },
        { key: 'detectionDate', label: 'Detection Date' },
      ]
    : isEntityHitsLayout
    ? [
        ...(showBulkSelection
          ? [
              {
                key: 'bulkSelect' as const,
                label: '',
                sortable: false,
              },
            ]
          : []),
        { key: 'name', label: 'Entity Name' },
        { key: 'country', label: 'Entity Country' },
        { key: 'lensCode', label: 'Lens' },
        { key: 'matchSource', label: 'List Name' },
        { key: 'sourceCountry', label: 'Source Country' },
        { key: 'score', label: 'Match Score' },
        { key: 'detectionDate', label: 'Date Updated' },
        ...(onToggleHitVisibility
          ? [
              {
                key: 'visibility' as const,
                label: 'Remove Hit',
                sortable: false,
              },
            ]
          : []),
      ]
    : isOwnershipTab
    ? [
        { key: 'name', label: 'Entity Name' },
        { key: 'entityType', label: isScreeningV2 ? 'Type' : 'Entity Type' },
        { key: 'relatedEntity', label: 'Related Entity' },
        { key: 'country', label: 'Country' },
        { key: 'lensCode', label: isScreeningV2 ? 'Lens' : 'BITE Lens' },
        { key: 'matchSource', label: 'List Name' },
        { key: 'score', label: 'Match Score' },
      ]
    : [
        { key: 'name', label: 'Entity Name' },
        { key: 'entityType', label: isScreeningV2 ? 'Type' : 'Entity Type' },
        { key: 'country', label: 'Country' },
        { key: 'lensCode', label: isScreeningV2 ? 'Lens' : 'BITE Lens' },
        { key: 'matchSource', label: 'List Name' },
        { key: 'score', label: 'Match Score' },
        {
          key: 'detectionDate',
          label: isScreeningV2 ? 'Detected' : 'Detection Date',
        },
      ];

  const handlePageChange = (nextPage: number) => {
    const resolvedPage = resolveEntityHitsPageChange({
      ...pagerMath,
      requestedPageIndex: nextPage,
      currentPageIndex: currentPage,
    });
    if (resolvedPage === null) {
      return;
    }
    setPage(resolvedPage);
    // Static data never calls the screening submit handler. For a
    // server-paginated static table, the parent page setter drives its query.
    if (!isStatic) {
      handleSubmit(resolvedPage, undefined, {
        runAiEvaluation: false,
        dataset: interactiveHitsDatasetForTab(resolvedActiveTab),
      });
    }
  };

  const handlePaginationChange = (
    _event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    handlePageChange(value - 1);
  };

  const handleOpenFilters = () => {
    setDraftTableFilters(tableFilters);
    setDraftScoreRange(scoreRange);
    setFilterSearches({
      entityType: '',
      country: '',
      lensCode: '',
      matchSource: '',
    });
    setFiltersOpen(true);
  };

  const toggleDraftFilterValue = (key: EntityFilterKey, value: string) => {
    setDraftTableFilters((prev) => {
      const selected = prev[key];
      const nextSelected = selected.includes(value)
        ? selected.filter((item) => item !== value)
        : [...selected, value];
      return { ...prev, [key]: nextSelected };
    });
  };

  // Hands a changed selection to the parent, which re-queries page 0 with it;
  // returns true so the caller knows not to refetch as well. Re-applying the
  // same filters publishes nothing.
  const publishTableFilters = (next: EntityTableFilterSelection): boolean => {
    if (!onTableFiltersChange || entityTableFiltersEqual(next, tableFilters)) {
      return false;
    }
    onTableFiltersChange(next);
    return true;
  };

  const applyTableFilters = (next: EntityTableFilterSelection) => {
    publishTableFilters(next);
    setTableFilters(next);
    setDraftTableFilters(next);
    setFiltersOpen(false);
  };

  const toggleSavedFilter = (filter: ISearchFilter) => {
    if (activeSavedFilter?.filter._id === filter._id) {
      setActiveSavedFilter(null);
      applyTableFilters(EMPTY_ENTITY_TABLE_FILTERS);
      return;
    }
    const { selection, hasLegacyParts } = fromSavedEntityFilter(filter);
    setActiveSavedFilter({
      filter: filter,
      selection: selection,
      hasLegacyParts: hasLegacyParts,
    });
    applyTableFilters(selection);
  };

  const updateActiveSavedFilter = async () => {
    if (!activeSavedFilter || !onUpdateSavedFilter) {
      return;
    }
    const selection = draftTableFilters;
    const saved = await onUpdateSavedFilter(activeSavedFilter.filter, selection);
    if (!saved) {
      return;
    }
    // Applied first: outside a batched update the mismatch effect would
    // otherwise drop the filter it is about to press.
    applyTableFilters(selection);
    setActiveSavedFilter({ ...activeSavedFilter, selection: selection });
  };

  const clearTableFilters = () => {
    const publishedToParent = publishTableFilters(EMPTY_ENTITY_TABLE_FILTERS);
    setTableFilters({
      entityType: [],
      country: [],
      lensCode: [],
      matchSource: [],
    });
    setDraftTableFilters({
      entityType: [],
      country: [],
      lensCode: [],
      matchSource: [],
    });
    setScoreRange(EMPTY_SCORE_RANGE);
    setDraftScoreRange(EMPTY_SCORE_RANGE);
    return publishedToParent;
  };

  const clearDraftTableFilters = () => {
    setDraftTableFilters({
      entityType: [],
      country: [],
      lensCode: [],
      matchSource: [],
    });
    setDraftScoreRange(EMPTY_SCORE_RANGE);
  };

  const handleApplyFilters = () => {
    publishTableFilters(draftTableFilters);
    setTableFilters(draftTableFilters);
    setScoreRange(draftScoreRange);
    setFiltersOpen(false);
  };

  const removeTableFilterValue = (key: EntityFilterKey, value: string) => {
    const next = {
      ...tableFilters,
      [key]: tableFilters[key].filter((item) => item !== value),
    };
    publishTableFilters(next);
    setTableFilters(next);
    setPage(0);
  };

  const renderLensBadges = (row: any) => {
    const lenses =
      isEntityHitsLayout && Array.isArray(row.lensNames) && row.lensNames.length
        ? row.lensNames
        : row.lensCode && row.lensCode !== '--'
        ? [row.lensValue || row.lensCode]
        : [];
    if (lenses.length === 0) {
      return <span className='entity-lens-badge'>--</span>;
    }
    return (
      <div className='entity-results-lens-stack'>
        {lenses.map((lens: string) => {
          const code = formatEntityLensCode(lens);
          return (
            <Tooltip
              key={lens}
              title={lens || 'No BITE Lens'}
              arrow
              placement='top'
            >
              <span className={getEntityLensBadgeClassName(code)}>{code}</span>
            </Tooltip>
          );
        })}
      </div>
    );
  };

  const renderCell = (row: any, key: TableColumnKey) => {
    switch (key) {
      case 'name':
        return (
          <td className='entity-results-name'>
            {isScreeningV2 ? (
              <button type='button' className='psc-row-open' title={row.name}>
                {row.name}
              </button>
            ) : (
              row.name
            )}
          </td>
        );
      case 'entityType':
        return <td>{row.entityType}</td>;
      case 'relatedEntity':
        return <td>{row.relatedEntity}</td>;
      case 'country':
        return <td>{row.country}</td>;
      case 'sourceCountry':
        return <td>{row.sourceCountry}</td>;
      case 'lensCode':
        return (
          <td className='entity-results-lens-cell'>{renderLensBadges(row)}</td>
        );
      case 'matchSource':
        return <td>{row.matchSource}</td>;
      case 'score':
        return (
          <td>
            {row.score === null ? (
              <span className='entity-score-empty'>—</span>
            ) : (
              <div className='entity-score-cell'>
                <span
                  className={`entity-score-line ${getScoreBand(row.score)}`}
                />
                <span className='entity-score-value'>{row.score}%</span>
              </div>
            )}
          </td>
        );
      case 'detectionDate':
        return (
          <td className={isScreeningV2 ? 'tnum' : undefined}>
            {formatDateEST(row.detectionDate)}
          </td>
        );
      case 'bulkSelect': {
        const selection = toBulkSelection(row);
        const isBulkSelected = Boolean(
          selection && bulkSelectedHits.has(selection.key),
        );
        const isBulkSelectionDisabled = Boolean(
          bulkOperation && bulkOperation !== (row.removed ? 'unhide' : 'hide'),
        );
        return (
          <td className='entity-results-bulk-cell'>
            <Checkbox
              checked={isBulkSelected}
              disabled={isBulkSelectionDisabled}
              onChange={() => handleToggleBulkHit(row)}
              onClick={(event) => event.stopPropagation()}
              size='small'
              inputProps={{
                'aria-label': `Select ${row.name || 'screening hit'}`,
              }}
              sx={{ color: 'var(--text-secondary)', padding: 0 }}
            />
          </td>
        );
      }
      case 'visibility':
        return (
          <td className='entity-results-vis-cell'>
            <button
              type='button'
              className='entity-results-vis-btn'
              aria-label={row.removed ? 'Show hit' : 'Hide hit'}
              onClick={(event) => {
                event.stopPropagation();
                if (!onToggleHitVisibility) {
                  return;
                }
                if (row.removed) {
                  onToggleHitVisibility(row.hitId, undefined, row.name);
                  return;
                }
                setDismissTarget({ id: row.hitId, name: row.name });
              }}
            >
              {row.removed ? (
                <VisibilityOffIcon sx={{ fontSize: 18 }} />
              ) : (
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='18'
                  height='18'
                  viewBox='0 0 18 18'
                  fill='none'
                  aria-hidden='true'
                >
                  <path
                    d='M1.81509 9.53488C1.71295 9.37315 1.66188 9.29229 1.63329 9.16756C1.61182 9.07387 1.61182 8.92613 1.63329 8.83244C1.66188 8.70771 1.71295 8.62685 1.81509 8.46512C2.65915 7.12863 5.17155 3.75 9.0003 3.75C12.8291 3.75 15.3415 7.12863 16.1855 8.46512C16.2877 8.62685 16.3387 8.70771 16.3673 8.83244C16.3888 8.92613 16.3888 9.07387 16.3673 9.16756C16.3387 9.29229 16.2877 9.37315 16.1855 9.53488C15.3415 10.8714 12.8291 14.25 9.0003 14.25C5.17155 14.25 2.65915 10.8714 1.81509 9.53488Z'
                    stroke='currentColor'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                  <path
                    d='M9.0003 11.25C10.2429 11.25 11.2503 10.2426 11.2503 9C11.2503 7.75736 10.2429 6.75 9.0003 6.75C7.75766 6.75 6.7503 7.75736 6.7503 9C6.7503 10.2426 7.75766 11.25 9.0003 11.25Z'
                    stroke='currentColor'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              )}
            </button>
          </td>
        );
      default:
        return <td>--</td>;
    }
  };

  const selectedSource = selectedHit ? getSource(selectedHit) : {};
  const { signals: selectedRiskSignals, rest: selectedNonSignals } =
    splitRiskSignals(selectedSource);
  const { meaning: selectedHitMeaning, rest: selectedDetails } =
    splitHitMeaning(selectedNonSignals);
  const selectedRowIndex = selectedHit
    ? sortedRows.findIndex((row: any) => row.hit === selectedHit)
    : -1;
  const selectedScore = selectedHit
    ? decoratedRows.find((row: any) => row.hit === selectedHit)?.score ?? null
    : null;
  const selectedDecoratedRow = selectedHit
    ? decoratedRows.find((row: any) => row.hit === selectedHit)
    : null;
  const canNavigatePrev = selectedRowIndex > 0;
  const canNavigateNext =
    selectedRowIndex >= 0 && selectedRowIndex < sortedRows.length - 1;
  const handleDrawerPrev = () => {
    if (canNavigatePrev) {
      setSelectedHit(sortedRows[selectedRowIndex - 1].hit);
    }
  };
  const handleDrawerNext = () => {
    if (canNavigateNext) {
      setSelectedHit(sortedRows[selectedRowIndex + 1].hit);
    }
  };

  const handleClearSearch = () => {
    lastSubmittedSearch.current = '';
    setSearchQuery('');
    setPage(0);
    // Static mode filters loaded rows client-side, so clearing the box is
    // enough; server mode re-runs the screen with the original entity name
    // (undefined, not '', to avoid an all-docs match).
    if (!isStatic) {
      handleSubmit(0, undefined, { runAiEvaluation: false });
    }
  };

  const handleResetResults = () => {
    lastSubmittedSearch.current = '';
    setSearchQuery('');
    setActiveTab('All Matches');
    setSortConfig({
      key: isEntityHitsLayout ? 'score' : '',
      direction: isEntityHitsLayout ? 'desc' : 'asc',
    });
    const refetchedByParent = clearTableFilters();
    setFiltersOpen(false);
    setSelectedHit(null);
    setIsDrawerOpen(false);
    setPage(0);
    // Reset only the table-level state (filters, sort, in-table search). In
    // server mode also restore the original screening results (undefined keeps
    // the screened entity name rather than re-querying with an empty term) —
    // unless dropping the query-side filters already triggered that refetch.
    // Dropping the screen's own filters re-runs the screen instead.
    if (screenFilterControls && screenFilterControls.count > 0) {
      screenFilterControls.onClear();
    } else if (!isStatic && !refetchedByParent) {
      handleSubmit(0, undefined, { runAiEvaluation: false });
    }
  };

  const tableActions = (
    <div
      className={`entity-results-actions${hideToolbar ? ' is-detached' : ''}`}
    >
      <button
        type='button'
        className={activeFilterCount > 0 ? 'active' : ''}
        onClick={handleOpenFilters}
      >
        <FilterListIcon sx={{ fontSize: 20 }} />
        Filters
        {activeFilterCount > 0 && <span>{activeFilterCount}</span>}
      </button>
      <button
        type='button'
        aria-label='Reset entity results table'
        onClick={handleResetResults}
      >
        <RefreshIcon sx={{ fontSize: 20 }} />
      </button>
      {onDownloadResults && (
        <button type='button' onClick={onDownloadResults}>
          <DownloadOutlinedIcon sx={{ fontSize: 20 }} />
          Download Results
        </button>
      )}
    </div>
  );

  const filterLabels: Record<EntityFilterKey, string> = {
    entityType: 'Type:',
    country: 'Country:',
    lensCode: 'Lens:',
    matchSource: 'List:',
  };
  const filterChips: FilterChip[] = (
    Object.keys(tableFilters) as EntityFilterKey[]
  ).reduce<FilterChip[]>(
    (chips, key) =>
      chips.concat(
        tableFilters[key].map((value) => ({
          key: `${key}-${value}`,
          group: filterLabels[key],
          label: value,
          onRemove: () => removeTableFilterValue(key, value),
        })),
      ),
    [],
  );

  const switchTab = (tab: string) => {
    if (tab === resolvedActiveTab) {
      return;
    }
    setActiveTab(tab);
    setPage(0);
    if (!isStatic) {
      handleSubmit(0, undefined, {
        runAiEvaluation: false,
        dataset: interactiveHitsDatasetForTab(tab),
      });
    }
  };

  const screeningToolbar = (
    <ResultsToolbar
      searchId='psc-entity-f'
      searchLabel='Filter these matches'
      placeholder='Filter these matches…'
      query={resolvedSearchQuery}
      onQuery={setSearchQuery}
      middle={
        <div
          className='psc-seg psc-seg--tabs'
          role='group'
          aria-label='Result set'
        >
          {Object.entries(tabCounts).map(([tab, count]) => {
            const loadingOwnership =
              tab === 'Ownership' && Boolean(ownershipResults?.isLoading);
            const hideCount = tab === 'Ownership' && Number(count) > 1000000;
            return (
              <button
                type='button'
                key={tab}
                aria-pressed={resolvedActiveTab === tab}
                onClick={() => switchTab(tab)}
              >
                {tab}
                {loadingOwnership ? (
                  <CircularProgress
                    size={11}
                    thickness={5}
                    style={{ color: 'currentColor' }}
                    aria-label='Ownership results loading'
                  />
                ) : hideCount ? null : (
                  <span className='n tnum'>
                    {Number(count).toLocaleString()}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      }
      filterCount={shownFilterCount}
      filterLabel={
        screenFilterControls
          ? screenFilterControls.label
          : activeSavedFilter?.filter.filterName
      }
      onOpenFilters={
        screenFilterControls ? screenFilterControls.onOpen : handleOpenFilters
      }
      actions={
        onExport && (
          <button
            type='button'
            className='psc-btn'
            onClick={() => onExport(getExportContext())}
          >
            <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />
            Export
          </button>
        )
      }
      onReset={handleResetResults}
      resetLabel='Refresh results'
      chips={screenFilterControls ? screenFilterControls.chips : filterChips}
      onClearFilters={
        screenFilterControls ? screenFilterControls.onClear : clearTableFilters
      }
    />
  );

  // built only while a record is open: it reads the selected record
  const renderRecordDrawerContent = () => (
    <>
      <div className='drawer-header entity-record-drawer-header'>
        <div className='entity-record-drawer-nav'>
          <button
            type='button'
            onClick={handleDrawerPrev}
            disabled={!canNavigatePrev}
            title={
              isProductTable ? 'Open previous product' : 'Open previous entity'
            }
          >
            <ChevronLeftIcon sx={{ fontSize: 22 }} />
          </button>
          <button
            type='button'
            onClick={handleDrawerNext}
            disabled={!canNavigateNext}
            title={isProductTable ? 'Open next product' : 'Open next entity'}
          >
            <ChevronRightIcon sx={{ fontSize: 22 }} />
          </button>
        </div>
        <h2
          className='drawer-title entity-record-drawer-title'
          id='entity-record-drawer-title'
        >
          <span className='entity-record-drawer-title-main'>
            {isProductTable
              ? getProductName(selectedSource)
              : getValue(selectedSource, ['Name of Entity'])}
          </span>
        </h2>
        {onToggleHitVisibility && selectedDecoratedRow && (
          <button
            type='button'
            className='es2-drawer-remove-hit'
            onClick={() => {
              if (selectedDecoratedRow.removed) {
                onToggleHitVisibility(
                  selectedDecoratedRow.hitId,
                  undefined,
                  selectedDecoratedRow.name,
                );
              } else {
                setDismissTarget({
                  id: selectedDecoratedRow.hitId,
                  name: selectedDecoratedRow.name,
                });
              }
            }}
          >
            {selectedDecoratedRow.removed ? 'Restore hit' : 'Remove hit'}
          </button>
        )}
        <button
          type='button'
          className='drawer-close-btn entity-record-drawer-close'
          onClick={() => setIsDrawerOpen(false)}
        >
          <CloseIcon sx={{ fontSize: 28 }} />
        </button>
      </div>
      <div className='drawer-body entity-record-drawer-body'>
        {selectedRiskSignals.length > 0 && (
          <div>
            <div className='drawer-section-title'>Risk Signals</div>
            <div className='drawer-kv-box entity-record-drawer-kv-box'>
              {selectedRiskSignals.map(({ label, value }) => (
                <div
                  className='drawer-kv-row entity-record-drawer-row'
                  key={label}
                >
                  <span className='drawer-kv-label'>{label}</span>
                  <span className='drawer-kv-value'>
                    {renderFieldWithLinks(formatFieldValue(value))}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        {selectedHitMeaning !== null && (
          <div>
            <div className='drawer-section-title'>What This Hit Means</div>
            <div className='drawer-kv-box entity-record-drawer-kv-box'>
              <p className='drawer-text-block entity-record-drawer-meaning'>
                {renderFieldWithLinks(formatFieldValue(selectedHitMeaning))}
              </p>
            </div>
          </div>
        )}
        <div>
          <div className='drawer-section-title'>
            {isProductTable ? 'Product Details' : 'Entity Details'}
          </div>
          <div className='drawer-kv-box entity-record-drawer-kv-box'>
            {Object.entries(selectedDetails)
              .filter(([, value]) => hasDisplayValue(value))
              .map(([label, value]) => (
                <div
                  className='drawer-kv-row entity-record-drawer-row'
                  key={label}
                >
                  <span className='drawer-kv-label'>{label}</span>
                  <span className='drawer-kv-value'>
                    {renderFieldWithLinks(formatFieldValue(value))}
                  </span>
                </div>
              ))}
            {!isProductTable && (
              <div
                className='drawer-kv-row entity-record-drawer-row'
                key='__match_score'
              >
                <span className='drawer-kv-label'>Match Score</span>
                <span className='drawer-kv-value'>
                  {selectedScore === null ? '—' : `${selectedScore}%`}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      {!hideToolbar && isScreeningV2 && screeningToolbar}
      {isScreeningV2 && activeSavedFilter?.hasLegacyParts && (
        <div className='es2-filter-legacy-note is-inline' role='status'>
          Some parts of this Saved Filter only applied before the screen and
          were skipped.
        </div>
      )}
      {!hideToolbar && !isScreeningV2 && (
        <>
          <div className='entity-results-toolbar'>
            <div className='entity-results-search'>
              <SearchIcon sx={{ fontSize: 19 }} />
              <input
                value={resolvedSearchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={
                  isScreeningV2
                    ? 'Filter these matches…'
                    : isProductTable
                    ? 'Search products, HTS, source, lens...'
                    : 'Search entities, source, lens...'
                }
                aria-label={
                  isScreeningV2
                    ? 'Filter these matches'
                    : 'Search entity results'
                }
              />
              {resolvedSearchQuery && (
                <button
                  type='button'
                  className='entity-results-search-clear'
                  aria-label='Clear entity results search'
                  onClick={handleClearSearch}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </button>
              )}
            </div>
            <div className='entity-results-tabs'>
              {Object.entries(tabCounts).map(([tab, count]) => {
                const showOwnershipSpinner =
                  tab === 'Ownership' && Boolean(ownershipResults?.isLoading);
                // DECISION: the ownership service can return the full BOI
                // index size; suppress that misleading badge until it is
                // confirmed to be scoped to this entity screen.
                const hideOwnershipCount =
                  isScreeningV2 &&
                  tab === 'Ownership' &&
                  Number(count) > 1000000;
                return (
                  <button
                    type='button'
                    key={tab}
                    className={resolvedActiveTab === tab ? 'active' : ''}
                    aria-pressed={
                      isScreeningV2 ? resolvedActiveTab === tab : undefined
                    }
                    onClick={() => {
                      if (tab === resolvedActiveTab) {
                        return;
                      }
                      // Jump back to page 1 of the selected dataset. Tool True
                      // Pagination keeps one server page in memory, so refetch that
                      // page instead of slicing a bulk-loaded set.
                      setActiveTab(tab);
                      setPage(0);
                      if (!isStatic) {
                        handleSubmit(0, undefined, {
                          runAiEvaluation: false,
                          dataset: interactiveHitsDatasetForTab(tab),
                        });
                      }
                    }}
                  >
                    {tab}
                    <span
                      className={
                        showOwnershipSpinner
                          ? 'entity-results-tab-loading-indicator'
                          : undefined
                      }
                    >
                      {showOwnershipSpinner ? (
                        <CircularProgress
                          size={12}
                          thickness={5}
                          style={{ color: 'currentColor' }}
                          aria-label='Ownership results loading'
                        />
                      ) : hideOwnershipCount ? null : (
                        Number(count).toLocaleString()
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className='entity-results-actions'>
              <button
                type='button'
                className={activeFilterCount > 0 ? 'active' : ''}
                onClick={handleOpenFilters}
                aria-haspopup='dialog'
              >
                <FilterListIcon sx={{ fontSize: 20 }} />
                Filters
                {activeFilterCount > 0 && <span>{activeFilterCount}</span>}
              </button>
              {isScreeningV2 && onExport && (
                <button
                  type='button'
                  onClick={() => onExport(getExportContext())}
                >
                  <DownloadOutlinedIcon sx={{ fontSize: 18 }} />
                  Export
                </button>
              )}
              <button
                type='button'
                aria-label={
                  isScreeningV2
                    ? 'Refresh results'
                    : 'Reset entity results table'
                }
                onClick={handleResetResults}
              >
                <RefreshIcon sx={{ fontSize: 20 }} />
              </button>
            </div>
          </div>
        </>
      )}
      {hideToolbar &&
        isEntityHitsLayout &&
        (actionsPortalTarget === undefined
          ? tableActions
          : actionsPortalTarget
          ? createPortal(tableActions, actionsPortalTarget)
          : null)}

      <Modal open={filtersOpen} onClose={() => setFiltersOpen(false)}>
        <div
          className={`entity-results-filter-modal${
            isScreeningV2 ? ' screening-v2' : ''
          }`}
          role={isScreeningV2 ? 'dialog' : undefined}
          aria-modal={isScreeningV2 ? 'true' : undefined}
          aria-labelledby={
            isScreeningV2 ? 'entity-results-filter-title' : undefined
          }
        >
          <div className='entity-results-filter-modal-header'>
            <div className='entity-results-filter-modal-header-content'>
              <div className='entity-results-filter-modal-icon'>
                <FilterListIcon sx={{ fontSize: 24 }} />
              </div>
              <div className='entity-results-filter-modal-copy'>
                <h2
                  id={isScreeningV2 ? 'entity-results-filter-title' : undefined}
                >
                  {isScreeningV2
                    ? 'Filter matches'
                    : isProductTable
                    ? 'Product Table Filters'
                    : 'Entity Table Filters'}
                </h2>
                <p>
                  {isScreeningV2
                    ? 'Applying filters re-runs the screen, including the AI Evaluation. The verdict boxes above will describe only the filtered matches.'
                    : isProductTable
                    ? 'Choose HTS codes, countries, BITE lenses, and list names to narrow the current table.'
                    : isEntityHitsLayout
                    ? 'Choose BITE lenses, list names, entity countries, and a match score range to narrow the current table.'
                    : filtersRunOnServer
                    ? 'Choose entity types, countries, BITE lenses, and list names to re-run the screen against every matching record. Match Score narrows the loaded page only.'
                    : 'Choose entity types, countries, BITE lenses, and match score bands to narrow the current table.'}
                </p>
              </div>
            </div>
            <button
              type='button'
              className='entity-results-filter-modal-close'
              aria-label={isScreeningV2 ? 'Close filters' : undefined}
              onClick={() => setFiltersOpen(false)}
            >
              <CloseIcon sx={{ fontSize: 28 }} />
            </button>
          </div>

          {isScreeningV2 && (
            <div className='es2-filter-band'>
              <div className='es2-saved-filter-band'>
                <strong>Saved filters</strong>
                {savedFilters.length ? (
                  savedFilters.map((filter) => {
                    const filterId = filter._id;
                    const isActive =
                      Boolean(filterId) &&
                      activeSavedFilter?.filter._id === filterId;
                    return (
                      <span
                        className={`es2-saved-filter-chip${
                          isActive ? ' active' : ''
                        }`}
                        key={filterId || filter.filterName}
                      >
                        <button
                          type='button'
                          aria-pressed={isActive}
                          onClick={() => toggleSavedFilter(filter)}
                        >
                          {filter.filterName}
                        </button>
                        {onDeleteSavedFilter && filterId && (
                          <button
                            type='button'
                            aria-label={`Delete ${filter.filterName}`}
                            onClick={() => {
                              if (isActive) {
                                setActiveSavedFilter(null);
                              }
                              onDeleteSavedFilter(filterId);
                            }}
                          >
                            <CloseIcon sx={{ fontSize: 12 }} />
                          </button>
                        )}
                      </span>
                    );
                  })
                ) : (
                  <span>No saved filters</span>
                )}
              </div>
              <div className='es2-as-of-band'>
                <strong>As of</strong>
                <button
                  type='button'
                  className={!asOfLabel ? 'active' : ''}
                  onClick={() => {
                    if (asOfLabel && onClearAsOfDate) {
                      setFiltersOpen(false);
                      onClearAsOfDate();
                    }
                  }}
                >
                  Today
                </button>
                <button
                  type='button'
                  className={asOfLabel ? 'active' : ''}
                  onClick={() => {
                    setFiltersOpen(false);
                    onOpenAsOfDate?.();
                  }}
                >
                  {asOfLabel || 'Choose date'}
                </button>
              </div>
            </div>
          )}
          <div className='entity-results-filter-modal-body'>
            {(isEntityHitsLayout
              ? [
                  {
                    key: 'lensCode' as EntityFilterKey | 'matchScore',
                    label: 'BITE Lens',
                    options: filterOptions.lensCode,
                  },
                  {
                    key: 'matchSource' as EntityFilterKey | 'matchScore',
                    label: 'List Name',
                    options: filterOptions.matchSource,
                  },
                  {
                    key: 'country' as EntityFilterKey | 'matchScore',
                    label: 'Entity Country',
                    options: filterOptions.country,
                  },
                  {
                    key: 'matchScore' as EntityFilterKey | 'matchScore',
                    label: 'Match Score',
                    options: [],
                  },
                ]
              : isScreeningV2
              ? [
                  // DECISION: match score remains client-computed and the
                  // service has no score-filter key, so D2 keeps four real
                  // server-backed columns instead of showing a fake band.
                  {
                    key: 'lensCode' as EntityFilterKey | 'matchScore',
                    label: 'Lens',
                    options: filterOptions.lensCode,
                  },
                  {
                    key: 'matchSource' as EntityFilterKey | 'matchScore',
                    label: 'List Name',
                    options: filterOptions.matchSource,
                  },
                  {
                    key: 'country' as EntityFilterKey | 'matchScore',
                    label: 'Entity Country',
                    options: filterOptions.country,
                  },
                  {
                    key: 'entityType' as EntityFilterKey | 'matchScore',
                    label: 'Entity Type',
                    options: filterOptions.entityType,
                  },
                ]
              : [
                  {
                    key: 'entityType' as EntityFilterKey | 'matchScore',
                    label: isProductTable ? 'HTS / HS Code' : 'Entity Type',
                    options: filterOptions.entityType,
                  },
                  {
                    key: 'country' as EntityFilterKey | 'matchScore',
                    label: 'Country',
                    options: filterOptions.country,
                  },
                  {
                    key: 'lensCode' as EntityFilterKey | 'matchScore',
                    label: 'BITE Lens',
                    options: filterOptions.lensCode,
                  },
                  {
                    key: 'matchSource' as EntityFilterKey | 'matchScore',
                    label: 'List Name',
                    options: filterOptions.matchSource,
                  },
                ]
            ).map((section) => (
              <div
                className='entity-results-filter-modal-column'
                key={section.key}
              >
                <div className='entity-results-filter-modal-column-header'>
                  <h3>{section.label}</h3>
                  {isScreeningV2 && section.key !== 'matchScore' && (
                    <span>
                      {draftTableFilters[section.key as EntityFilterKey].length
                        ? `${
                            draftTableFilters[section.key as EntityFilterKey]
                              .length
                          } selected`
                        : `All ${section.options.length}`}
                    </span>
                  )}
                </div>
                {isScreeningV2 && section.key !== 'matchScore' && (
                  <div className='es2-filter-column-search'>
                    <SearchIcon sx={{ fontSize: 16 }} />
                    <input
                      value={filterSearches[section.key as EntityFilterKey]}
                      onChange={(event) =>
                        setFilterSearches((previous) => ({
                          ...previous,
                          [section.key]: event.target.value,
                        }))
                      }
                      placeholder={`Search ${String(
                        section.label,
                      ).toLowerCase()}s`}
                      aria-label={`Search ${String(
                        section.label,
                      ).toLowerCase()}s`}
                    />
                  </div>
                )}
                {section.key === 'matchScore' ? (
                  <div className='entity-results-filter-modal-options'>
                    <label className='entity-results-filter-score-field'>
                      Minimum %
                      <input
                        type='number'
                        min={0}
                        max={100}
                        value={draftScoreRange.min}
                        placeholder='Any'
                        onChange={(event) =>
                          setDraftScoreRange((previous) => ({
                            ...previous,
                            min: event.target.value,
                          }))
                        }
                      />
                    </label>
                    <label className='entity-results-filter-score-field'>
                      Maximum %
                      <input
                        type='number'
                        min={0}
                        max={100}
                        value={draftScoreRange.max}
                        placeholder='Any'
                        onChange={(event) =>
                          setDraftScoreRange((previous) => ({
                            ...previous,
                            max: event.target.value,
                          }))
                        }
                      />
                    </label>
                  </div>
                ) : (
                  <div className='entity-results-filter-modal-options'>
                    {section.options.filter(
                      (option: any) =>
                        !isScreeningV2 ||
                        section.key === 'matchScore' ||
                        String(option)
                          .toLowerCase()
                          .includes(
                            filterSearches[
                              section.key as EntityFilterKey
                            ].toLowerCase(),
                          ),
                    ).length > 0 ? (
                      section.options
                        .filter(
                          (option: any) =>
                            !isScreeningV2 ||
                            section.key === 'matchScore' ||
                            String(option)
                              .toLowerCase()
                              .includes(
                                filterSearches[
                                  section.key as EntityFilterKey
                                ].toLowerCase(),
                              ),
                        )
                        .map((option: any) => {
                          const label = option;

                          return (
                            <button
                              type='button'
                              key={option}
                              className='entity-results-filter-modal-option'
                              role={isScreeningV2 ? 'checkbox' : undefined}
                              aria-checked={
                                isScreeningV2
                                  ? draftTableFilters[
                                      section.key as EntityFilterKey
                                    ].includes(option)
                                  : undefined
                              }
                              onClick={() =>
                                toggleDraftFilterValue(
                                  section.key as EntityFilterKey,
                                  option,
                                )
                              }
                            >
                              <span className='bill-container-fit-screen-content-row-input'>
                                <span
                                  className={`bill-container-fit-screen-content-row-input-icon ${
                                    draftTableFilters[
                                      section.key as EntityFilterKey
                                    ].includes(option)
                                      ? 'selected'
                                      : ''
                                  }`}
                                >
                                  <span
                                    className={`bill-container-fit-screen-content-row-input-icon-rnd ${
                                      draftTableFilters[
                                        section.key as EntityFilterKey
                                      ].includes(option)
                                        ? 'selected'
                                        : ''
                                    }`}
                                  />
                                </span>
                              </span>
                              <span className='entity-results-filter-modal-option-text'>
                                {label}
                              </span>
                            </button>
                          );
                        })
                    ) : (
                      <p>
                        {isScreeningV2
                          ? `Nothing here matches “${
                              filterSearches[section.key as EntityFilterKey]
                            }”.`
                          : 'No options on this page'}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className='entity-results-filter-modal-footer'>
            {isScreeningV2 && (
              <span className='es2-filter-preview'>
                {countEntityTableFilters(draftTableFilters)} filters selected
              </span>
            )}
            <button type='button' onClick={clearDraftTableFilters}>
              {isScreeningV2 ? 'Clear all' : 'Clear'}
            </button>
            {isScreeningV2 && onSaveFilterRequest && (
              <button
                type='button'
                disabled={countEntityTableFilters(draftTableFilters) === 0}
                onClick={() => {
                  setFiltersOpen(false);
                  onSaveFilterRequest(draftTableFilters);
                }}
              >
                Save as filter
              </button>
            )}
            {isScreeningV2 && activeSavedFilter && onUpdateSavedFilter && (
              <button type='button' onClick={updateActiveSavedFilter}>
                Update saved
              </button>
            )}
            {isScreeningV2 && (
              <button type='button' onClick={() => setFiltersOpen(false)}>
                Cancel
              </button>
            )}
            <button type='button' onClick={handleApplyFilters}>
              {isScreeningV2 ? 'Show matches' : 'Apply'}
            </button>
          </div>
        </div>
      </Modal>

      <div
        className={`entity-results-table-card${
          isScreeningV2 ? ' screening-v2 psc-table-card psc-settle' : ''
        }`}
      >
        {activeResultsLoading && (
          <div className='entity-results-table-loading'>
            <CircularProgress
              size={34}
              style={{ color: 'var(--action-color)' }}
            />
          </div>
        )}
        <div className='entity-results-table-scroll'>
          <table
            className={`entity-results-table${
              isOwnershipTab ? ' ownership' : ''
            }`}
          >
            <thead>
              <tr>
                {columnHeaders.map((column) => (
                  <th
                    key={column.key}
                    className={
                      column.key === 'visibility'
                        ? 'entity-results-vis-th'
                        : column.key === 'bulkSelect'
                        ? 'entity-results-bulk-th'
                        : column.key === 'lensCode' && isEntityHitsLayout
                        ? 'entity-results-lens-th'
                        : undefined
                    }
                  >
                    {column.key === 'bulkSelect' ? (
                      <span className='entity-results-sort-header'>
                        <Checkbox
                          checked={isPageSelected}
                          indeterminate={isPageIndeterminate}
                          onChange={handleTogglePageSelection}
                          onClick={(event) => event.stopPropagation()}
                          size='small'
                          inputProps={{
                            'aria-label': `Select all hits to ${pageSelectionOperation}`,
                          }}
                          sx={{ color: 'var(--text-secondary)', padding: 0 }}
                        />
                      </span>
                    ) : column.sortable === false ? (
                      <span className='entity-results-sort-header'>
                        {column.label}
                      </span>
                    ) : (
                      <button
                        type='button'
                        className='entity-results-sort-header'
                        onClick={() => handleSort(column.key as EntitySortKey)}
                      >
                        {column.label}
                        {getSortIcon(column.key as EntitySortKey)}
                      </button>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pagedRows.map((row: any, index: number) => {
                const isSelected = selectedHit === row.hit;
                return (
                  <tr
                    key={`${row.name}-${index}`}
                    className={[
                      isSelected ? 'selected-row' : '',
                      row.removed ? 'entity-results-row-hidden' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() => {
                      setSelectedHit(row.hit);
                      setIsDrawerOpen(true);
                    }}
                    style={
                      isScreeningV2
                        ? {
                            animationDelay: `${
                              220 + Math.min(index * 26, 210)
                            }ms`,
                          }
                        : undefined
                    }
                  >
                    {columnHeaders.map((column) => (
                      <React.Fragment key={column.key}>
                        {renderCell(row, column.key)}
                      </React.Fragment>
                    ))}
                  </tr>
                );
              })}
              {sortedRows.length === 0 && (
                <tr>
                  <td
                    colSpan={columnHeaders.length}
                    className='entity-results-empty'
                  >
                    {isScreeningV2 &&
                    screenFilterControls &&
                    screenFilterControls.count > 0 ? (
                      <div className='es2-empty-state'>
                        <strong>No matches with these filters</strong>
                        <span>
                          This screen ran against your filters only, not every
                          list.
                        </span>
                        <button
                          type='button'
                          onClick={screenFilterControls.onClear}
                        >
                          Clear all filters
                        </button>
                      </div>
                    ) : isScreeningV2 && activeFilterCount > 0 ? (
                      <div className='es2-empty-state'>
                        <strong>No matches with these filters</strong>
                        <span>
                          All {totalForPagination.toLocaleString()} matches were
                          still screened — the filters only narrow what is
                          listed here.
                        </span>
                        <button type='button' onClick={clearTableFilters}>
                          Clear all filters
                        </button>
                      </div>
                    ) : isProductTable ? (
                      'No product matches found.'
                    ) : (
                      'No entity matches found.'
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {showBulkSelection && bulkSelections.length > 0 && (
          <div className='bulk-hit-action-tray' role='status'>
            <div className='bulk-hit-action-tray__summary'>
              <strong>{bulkSelections.length}</strong>
              <span>
                hit{bulkSelections.length === 1 ? '' : 's'} selected across{' '}
                {selectedEntityCount} entit
                {selectedEntityCount === 1 ? 'y' : 'ies'} to {bulkOperation}
              </span>
            </div>
            <div className='bulk-hit-action-tray__actions'>
              <button
                type='button'
                className='bulk-hit-action-tray__clear'
                onClick={handleClearBulkSelection}
                disabled={isBulkHiding}
              >
                Clear
              </button>
              <button
                type='button'
                className='bulk-hit-action-tray__review'
                onClick={() => setIsBulkReviewOpen(true)}
                disabled={isBulkHiding}
              >
                Review &amp; {bulkOperation}
              </button>
            </div>
          </div>
        )}

        <div className='entity-results-footer'>
          <span>
            {isScreeningV2 ? (
              <>
                Showing {pagedRows.length.toLocaleString()} of{' '}
                {totalForPagination.toLocaleString()} matches
                {shownFilterCount > 0
                  ? ` · ${shownFilterCount} filter${
                      shownFilterCount === 1 ? '' : 's'
                    }`
                  : ''}
              </>
            ) : isPageScopedFiltering ? (
              <>
                Showing {pagedRows.length.toLocaleString()} matching results on
                this page · {totalForPagination.toLocaleString()} total results
              </>
            ) : (
              <>
                Showing {sortedRows.length ? pageStart + 1 : 0} to{' '}
                {Math.min(pageStart + pagedRows.length, totalForPagination)} of{' '}
                {totalForPagination.toLocaleString()} results
              </>
            )}
          </span>
          <div className='entity-results-pagination hb-pagination'>
            <Pagination
              count={pageCount}
              page={currentPage + 1}
              onChange={handlePaginationChange}
              color='primary'
              siblingCount={1}
              boundaryCount={1}
            />
          </div>
        </div>
      </div>

      {selectedHit &&
        isDrawerOpen &&
        (isScreeningV2 ? (
          // In the screening tools the page is its own stacking layer under the app header,
          // so the drawer mounts in the dialog layer above it, as the import and export
          // record drawer does. It also gains Escape to close and a focus trap.
          <ScreeningDialog
            labelledBy='entity-record-drawer-title'
            panelClassName='drawer-panel entity-record-drawer psc-record-drawer'
            onClose={() => setIsDrawerOpen(false)}
          >
            {renderRecordDrawerContent()}
          </ScreeningDialog>
        ) : (
          <div
            className='drawer-backdrop'
            onClick={() => setIsDrawerOpen(false)}
          >
            <aside
              className='drawer-panel entity-record-drawer'
              onClick={(e) => e.stopPropagation()}
            >
              {renderRecordDrawerContent()}
            </aside>
          </div>
        ))}
      {showBulkSelection && (
        <BulkDismissHitsModal
          open={isBulkReviewOpen}
          selections={bulkSelections}
          isProcessing={isBulkHiding}
          onClose={() => setIsBulkReviewOpen(false)}
          onRemove={handleRemoveBulkSelection}
          onConfirm={handleConfirmBulkUpdate}
          operation={bulkOperation || 'hide'}
        />
      )}
      {onToggleHitVisibility && (
        <DismissHitModal
          open={Boolean(dismissTarget)}
          recordName={dismissTarget?.name}
          onClose={() => setDismissTarget(null)}
          onConfirm={(reason) => {
            if (!dismissTarget) {
              return;
            }
            onToggleHitVisibility(
              dismissTarget.id,
              reason || undefined,
              dismissTarget.name,
            );
            setDismissTarget(null);
          }}
        />
      )}
    </>
  );
};

export default EntityScreeningResultsTable;
